import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { MatIcon } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'

import moment from 'moment'
import { Subject, tap, finalize, switchMap, catchError, of } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { AvatarComponent } from '../../../components/avatar/avatar.component'
import { ButtonComponent } from '../../../components/button/button.component'
import { LoadingComponent } from '../../../components/loading/loading.component'
import { MonthYearInputComponent } from '../../../components/year-month-input/month-year-input.component'
import { License } from '../../../services/license/license.mapping'
import { LicenseService } from '../../../services/license/license.service'
import { CsvExportService } from '../../../services/csv-export/csv-export.service'
import { getTitle } from '../../../util/accessibility'
import { captureException } from '../../../util/captureException'
import { CustomError } from '../../../util/throwIfCustomError'
import {
  buildLicenseUsageCsvFileName,
  LICENSE_USAGE_CSV_HEADERS,
  type LicenseUsageCsvRow,
  toLicenseUsageCsvData,
} from '../../../util/file/license-usage-csv'

interface LicenseRow extends LicenseUsageCsvRow {
  thumbnailPath?: string
}

@UntilDestroy()
@Component({
  selector: 'app-license-usage-page',
  standalone: true,
  imports: [
    ButtonComponent,
    MatIcon,
    MatTableModule,
    AvatarComponent,
    MonthYearInputComponent,
    LoadingComponent,
  ],
  templateUrl: './license-usage-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './license-usage-page.component.scss',
})
export class LicenseUsagePageComponent implements OnInit {
  displayedColumns: string[] = [
    'name',
    'department',
    'reportTo',
    'businessMeetingCount',
    'licenseUsage',
    'extraChargeCount',
  ]
  dataSource: LicenseRow[] = []
  selectedDate = new Date()
  selectedDate$ = new Subject<Date>()
  isLoading = false

  constructor(
    private licenseService: LicenseService,
    private titleService: Title,
    private csvExportService: CsvExportService,
  ) {
    this.titleService.setTitle(getTitle('ライセンス一覧'))
  }

  ngOnInit(): void {
    this.selectedDate$
      .pipe(
        tap(() => {
          this.dataSource = []
        }),
        switchMap((date) => {
          // 前のリクエストの finalize が完了してから読み込み状態にする。
          this.isLoading = true
          return this.licenseService
            .searchLicenses(moment(date).format('YYYYMM'))
            .pipe(
              catchError((error: unknown) => {
                if (!(error instanceof CustomError)) {
                  captureException(error)
                }
                return of([] as License[])
              }),
              finalize(() => {
                this.isLoading = false
              }),
            )
        }),
        untilDestroyed(this),
      )
      .subscribe((licenses) => {
        this.dataSource = licenses.map(this.mappingToRow)
      })
    this.selectedDate$.next(this.selectedDate)
  }

  onDateChange(date: Date): void {
    this.selectedDate = date
    this.selectedDate$.next(date)
  }

  /** 読み込みが完了し、出力対象の行がある場合だけエクスポートを許可する。 */
  get canExport(): boolean {
    return !this.isLoading && this.dataSource.length > 0
  }

  /** 表示中のライセンス利用状況を選択月の BOM 付き CSV としてダウンロードする。 */
  exportCsv(): void {
    if (!this.canExport) {
      return
    }

    this.csvExportService.downloadCsv(
      buildLicenseUsageCsvFileName(this.selectedDate),
      LICENSE_USAGE_CSV_HEADERS,
      toLicenseUsageCsvData(this.dataSource),
    )
  }

  formatHours(seconds: number): string {
    return `${Math.floor(seconds / 3600)}時間`
  }

  formatHoursAndMinutes(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}時間${minutes}分`
  }

  formatMonthYearRange(date: Date): string {
    const start = moment(date).startOf('month').format('YYYY/MM/DD')
    const end = moment(date).endOf('month').format('YYYY/MM/DD')
    return `${start} - ${end}`
  }

  calculateUsagePercentage(usedSeconds: number, limitSeconds: number): number {
    if (limitSeconds === 0) {
      return 0
    }

    return Math.min(Math.ceil((usedSeconds / limitSeconds) * 100), 100)
  }

  private mappingToRow(license: License): LicenseRow {
    return {
      name: license.user?.username,
      email: license.user?.email,
      thumbnailPath: license.user?.thumbnailPath || undefined,
      department: license.user?.departmentName || undefined,
      reportTo: license.user?.reportTo || undefined,
      businessMeetingCount: license.businessMeetingCount,
      usedSeconds: license.usedSeconds,
      limitSeconds: license.limitSeconds,
      extraChargeCount: license.extraChargeCount,
    }
  }
}
